import AWS from "aws-sdk";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

AWS.config.update({
  region: process.env.REACT_APP_UAT_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_UAT_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_UAT_AWS_SECRET_ACCESS_KEY,
  },
});

const getApiConfigForTool = (selectedTool) => {
  const toolConfig = {
    "Data Matching": {
      baseFolderPath: "matching-activity/",
      apiEndpoint: "match_activity",
      alias: "data_match",
    },
    Classification: {
      baseFolderPath: "material-classification/",
      apiEndpoint: "classification",
      alias: "uat_classify",
    },
    Enrichment: {
      baseFolderPath: "enrichment/",
      apiEndpoint: "enrichment",
      alias: "uat_enrich",
    },
    "Mpn Segregation": {
      baseFolderPath: "mpn-segregation/",
      apiEndpoint: "mpn_segregation",
      alias: "uat_mpn",
    },
    "alternate Manufacturer": {
      baseFolderPath: "alternate-manufacturer/",
      apiEndpoint: "alternate_manufacturer",
      alias: "alt_manf",
    },
    "Duplicate Check": {
      baseFolderPath: "duplicate-check/",
      apiEndpoint: "duplicate_check",
      alias: "uat_dupchk",
    },
  };

  return (
    toolConfig[selectedTool] || {
      baseFolderPath: `${selectedTool.toLowerCase().replace(/\s+/g, "-")}/`,
      apiEndpoint: selectedTool.toLowerCase().replace(/\s+/g, "_"),
      alias: "",
    }
  );
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const timeout = setTimeout(() => {
      reader.abort();
      reject(new Error("File reading timed out. The file may be too large."));
    }, 60000);

    reader.onload = () => {
      clearTimeout(timeout);
      try {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      } catch (error) {
        reject(new Error("Failed to process file as base64"));
      }
    };

    reader.onerror = (error) => {
      clearTimeout(timeout);
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

export const prepareAutomationInput = async (selectedTool, payload) => {
  try {
    const token = localStorage.getItem("token");
    if (!token)
      throw new Error("Authentication token not found. Please log in again.");

    const { alias, apiEndpoint } = getApiConfigForTool(selectedTool);
    const apiUrl = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/prepare_input`;

    console.log("Prepare Automation Input - Tool:", selectedTool);
    console.log("Prepare Automation Input - Payload:", payload);
    console.log("Prepare Automation Input - Full URL:", apiUrl);

    const response = await axios.post(apiUrl, payload, {
      headers: { "Content-Type": "application/json", "x-api-key": token },
      timeout: 5 * 60 * 1000,
    });

    if (response === undefined) throw new Error("No response from server");

    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204)
        throw new Error(response.data.message || "No content");
      return response.data;
    }
  } catch (error) {
    console.error("Prepare Automation Input error:", error);
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else if (error.message) sendError = new Error(error.message);
    else
      sendError = new Error(
        "Something unexpected happened during automation preparation. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

export const processE2EAutomation = async (selectedTool, payload) => {
  const toolConfig = {
    Classification: { alias: "uat_classify", endpoint: "classification" },
    "Data Matching": { alias: "data_match", endpoint: "match_activity" },
    Enrichment: { alias: "uat_enrich", endpoint: "enrichment" },
    "Mpn Segregation": { alias: "uat_mpn", endpoint: "mpn_segregation" },
  };

  const config = toolConfig[selectedTool];
  const BASE_URL =
    process.env.REACT_APP_BASE_URL || "https://devai.conasservices.com/v1";
  const url = `${BASE_URL}/${config.alias}/${config.endpoint}/process_e2e`;
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  console.log("E2E Automation - Tool:", selectedTool);
  console.log("E2E Automation - Full URL:", url);
  console.log("E2E Request Payload:", payload);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(url, {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json", "x-api-key": token },
      body: JSON.stringify(payload),
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorDetail;
      try {
        errorDetail = await response.json();
      } catch {
        errorDetail = { detail: response.statusText };
      }

      const err = new Error(`HTTP ${response.status}`);
      err.response = { status: response.status, data: errorDetail };
      throw err;
    }

    return await response.json();
  } catch (err) {
    clearTimeout(timeoutId);

    if (err.name === "AbortError") {
      const timeoutErr = new Error("Request timeout after 25s");
      timeoutErr.code = "ECONNABORTED";
      throw timeoutErr;
    }

    console.error("E2E Automation error:", err);
    throw err;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// checkEnrichmentE2E — calls /input_checke2e after Enrichment E2E processing
// Updates DataOperationHistory counts and status before navigating to history
// ─────────────────────────────────────────────────────────────────────────────
export const checkEnrichmentE2E = async (batchId) => {
  try {
    const token = localStorage.getItem("token");
    if (!token)
      throw new Error("Authentication token not found. Please log in again.");

    const { alias, apiEndpoint } = getApiConfigForTool("Enrichment");
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_checke2e`;

    console.log("checkEnrichmentE2E - batch_id:", batchId);
    console.log("checkEnrichmentE2E - URL:", url);

    const response = await axios.post(
      url,
      { data: [{ folderId: batchId }] },
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response from server");

    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204)
        throw new Error(response.data.message || "No content");
      console.log("checkEnrichmentE2E response:", response.data);
      return response.data;
    }
  } catch (error) {
    console.error("checkEnrichmentE2E error:", error);
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else if (error.message) sendError = new Error(error.message);
    else
      sendError = new Error(
        "Something unexpected happened during E2E input check.",
      );
    throw sendError;
  }
};

export const ingestFromS3Streaming = async (batchId, s3Key, onProgress) => {
  const token = localStorage.getItem("token");
  if (!token)
    throw new Error("Authentication token not found. Please log in again.");

  const url = `https://devai.conasservices.com/v1/uat_mpn/mpn_segregation/input_file/ingest_from_s3`;

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-api-key": token },
    body: JSON.stringify({ batch_id: batchId, s3_key: s3Key }),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Ingest request failed with status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop(); // keep incomplete last line for next read

    for (const line of lines) {
      if (!line.trim()) continue;
      const progress = JSON.parse(line);

      console.log("Ingest progress:", progress);
      if (onProgress) onProgress(progress); // ✅ loader updates here, per chunk

      if (progress.status === "completed" || progress.status === "error") {
        finalResult = progress;
      }
    }
  }

  if (finalResult?.status === "error") {
    throw new Error(finalResult.detail || "Ingestion failed");
  }

  return finalResult; // final "completed" summary
};

// ─────────────────────────────────────────────────────────────────────────────
// processRecordsStreaming — single process_records call; backend streams
// newline-delimited JSON progress updates (started/processing/completed)
// over one open connection.
// ─────────────────────────────────────────────────────────────────────────────
export const processRecordsStreaming = async (
  selectedTool,
  data,
  selectedModel,
  onProgress,
) => {
  const token = localStorage.getItem("token");
  if (!token)
    throw new Error("Authentication token not found. Please log in again.");

  const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
  const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/process_records`;

  const payload = {
    ...data,
    model_name: selectedModel,
    reference_batch_id: data?.data?.[0]?.folderId,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-api-key": token },
    body: JSON.stringify(payload),
  });

  if (!response.ok || !response.body) {
    let errBody;
    try {
      errBody = await response.json();
    } catch {
      errBody = { detail: response.statusText };
    }
    const err = new Error(
      errBody.detail ||
        errBody.message ||
        `Request failed with status ${response.status}`,
    );
    err.response = { status: response.status, data: errBody };
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop(); // keep incomplete last line for next read

    for (const line of lines) {
      if (!line.trim()) continue;
      const progress = JSON.parse(line);

      console.log("process_records progress:", progress);
      if (onProgress) onProgress(progress);

      if (progress.status === "completed" || progress.status === "error") {
        finalResult = progress;
      }
    }
  }

  if (finalResult?.status === "error") {
    throw new Error(
      finalResult.detail || finalResult.message || "Processing failed",
    );
  }

  return finalResult;
};

// ─────────────────────────────────────────────────────────────────────────────
// checkE2EInput — calls /input_checke2e after E2E processing for any tool
// Updates DataOperationHistory counts and status
// ─────────────────────────────────────────────────────────────────────────────
export const checkE2EInput = async (selectedTool, batchId) => {
  try {
    const token = localStorage.getItem("token");
    if (!token)
      throw new Error("Authentication token not found. Please log in again.");

    const { alias, apiEndpoint } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_checke2e`;

    console.log("checkE2EInput - tool:", selectedTool, "batch_id:", batchId);
    console.log("checkE2EInput - URL:", url);

    const response = await axios.post(
      url,
      { data: [{ folderId: batchId }] },
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response from server");
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204)
        throw new Error(response.data.message || "No content");
      console.log("checkE2EInput response:", response.data);
      return response.data;
    }
  } catch (error) {
    console.error("checkE2EInput error:", error);
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else if (error.message) sendError = new Error(error.message);
    else
      sendError = new Error(
        "Something unexpected happened during E2E input check.",
      );
    throw sendError;
  }
};

export const ingestFromS3Chunked = async (
  batchId,
  s3Key,
  chunkSize = 5000,
  onProgress,
) => {
  const token = localStorage.getItem("token");
  if (!token)
    throw new Error("Authentication token not found. Please log in again.");

  const url = `https://devai.conasservices.com/v1/uat_mpn/mpn_segregation/input_file/ingest_from_s3`;

  let limit = 0;
  let offset = chunkSize;
  let hasMore = true;
  let finalResult = null;

  while (hasMore) {
    const response = await axios.post(
      url,
      { batch_id: batchId, s3_key: s3Key, limit, offset },
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    const result = response.data;
    console.log("Ingest chunk progress:", result);

    if (onProgress) onProgress(result); // ✅ loader updates here, after every chunk resolves

    hasMore = result.has_more;
    finalResult = result;

    if (hasMore) {
      limit = offset;
      offset = Math.min(offset + chunkSize, result.total_records_in_file);
    }
  }

  return finalResult;
};

export const uploadAutomationData = async (
  selectedFiles,
  selectedTool,
  selectedModel,
  uniqueRecords,
  totalCount,
  folderId,
) => {
  try {
    if (!selectedFiles || selectedFiles.length === 0)
      throw new Error("No files selected");

    const uploadResults = [];
    const { baseFolderPath, apiEndpoint, alias } =
      getApiConfigForTool(selectedTool);
    const token = localStorage.getItem("token");
    if (!token)
      throw new Error("Authentication token not found. Please log in again.");

    let apiTimeout;
    if (totalCount <= 10000) apiTimeout = 10 * 60 * 1000;
    else if (totalCount <= 20000) apiTimeout = 15 * 60 * 1000;
    else apiTimeout = 20 * 60 * 1000;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileExtension = file.name.split(".").pop();

      let base64String;
      try {
        base64String = await fileToBase64(file);
      } catch (error) {
        throw new Error(`Failed to convert file to base64: ${error.message}`);
      }

      // const payload = {
      //   filename: file.name,
      //   tool_name: selectedTool,
      //  if (selectedTool === "Enrichment") model_name: selectedModel,
      // };

      let payload;

      if (
        selectedTool === "Enrichment" ||
        selectedTool === "Duplicate Check" ||
        selectedTool === "Classification"
      ) {
        payload = {
          filename: file.name,
          base64File: base64String,
          custom_batch_id: folderId,
          tool_name: selectedTool,
          model_name: selectedModel,
          unique_records: uniqueRecords,
          total_records: totalCount,
        };
      } else {
        payload = {
          filename: file.name,
          tool_name: selectedTool,
          model_name: selectedModel,
        };
      }

      // const apiUrl = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_file/presign`;
      let apiUrl;

      if (
        selectedTool === "Enrichment" ||
        selectedTool === "Duplicate Check" ||
        selectedTool === "Classification"
      ) {
        apiUrl = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_file`;
      } else {
        apiUrl = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_file/presign`;
      }
      const apiResponse = await axios.post(apiUrl, payload, {
        headers: { "Content-Type": "application/json", "x-api-key": token },
        timeout: apiTimeout,
        validateStatus: (status) => status >= 200 && status < 300,
      });

      if (
        apiResponse &&
        apiResponse.status >= 200 &&
        apiResponse.status < 300
      ) {
        if (
          selectedTool === "Enrichment" ||
          selectedTool === "Duplicate Check" ||
          selectedTool === "Classification"
        ) {
          uploadResults.push({
            originalFile: file.name,
            fileSize: file.size,
            fileType: file.type,
            folderId: apiResponse.data.batch_id,
            activityType: selectedTool,
          });
        } else if (selectedTool === "Mpn Segregation") {
          // Upload using backend-generated presigned URL
          await axios.put(apiResponse.data.upload_url, file, {
            headers: {
              "Content-Type": apiResponse.data.content_type,
            },
          });
          // const ingestResult = await ingestFromS3Streaming(
          //   apiResponse.data.batch_id,
          //   apiResponse.data.s3_key,
          //   (progress) => {
          //     // update your loader/progress bar here
          //     if (progress.status === "processing") {
          //       console.log(
          //         `Processed ${progress.start_record}-${progress.end_record} of ${progress.total_records} (${progress.progress_percentage}%)`,
          //       );
          //       if (window.mpnProgressCallback)
          //         window.mpnProgressCallback(progress);
          //     }
          //   },
          // );

          const ingestResult = await ingestFromS3Chunked(
            apiResponse.data.batch_id,
            apiResponse.data.s3_key,
            5000,
            (progress) => {
              if (window.mpnProgressCallback)
                window.mpnProgressCallback(progress);
              console.log(
                `Ingested ${progress.start_record}-${progress.end_record} of ${progress.total_records_in_file} (${progress.progress_percentage}%)`,
              );
            },
          );

          uploadResults.push({
            originalFile: file.name,
            fileSize: file.size,
            fileType: file.type,
            s3Location: apiResponse.data.s3_key,
            key: apiResponse.data.s3_key,
            folderId: apiResponse.data.batch_id,
            activityType: selectedTool,
            databaseOnly: true,
          });
        } else {
          const key = `${baseFolderPath}${folderId}/input_file.${fileExtension}`;
          const s3 = new AWS.S3();
          const uploadParams = {
            Bucket: process.env.REACT_APP_UAT_AWS_BUCKET,
            Key: key,
            ContentType: file.type,
            Expires: 300,
          };

          let presignedUrl;
          try {
            presignedUrl = await new Promise((resolve, reject) => {
              s3.getSignedUrl("putObject", uploadParams, (err, url) => {
                if (err) {
                  reject(err);
                  return;
                }
                resolve(url);
              });
            });
          } catch (error) {
            throw new Error(
              `S3 pre-signed URL generation failed: ${error.message}`,
            );
          }

          try {
            const uploadResponse = await fetch(presignedUrl, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": file.type },
            });
            if (!uploadResponse.ok)
              throw new Error(
                `S3 upload failed with status: ${uploadResponse.status}`,
              );
          } catch (error) {
            throw new Error(`Failed to upload file to S3: ${error.message}`);
          }

          const s3Url = `https://${uploadParams.Bucket}.s3.amazonaws.com/${key}`;
          uploadResults.push({
            originalFile: file.name,
            fileSize: file.size,
            fileType: file.type,
            s3Location: s3Url,
            key,
            folderId,
            activityType: selectedTool,
          });
        }
      } else {
        throw new Error(
          `File validation failed. API returned status ${apiResponse ? apiResponse.status : "unknown"}`,
        );
      }
    }

    return { status: 201, data: uploadResults };
  } catch (error) {
    throw error;
  }
};

export const processAutomationData = async (
  selectedTool,
  data,
  selectedModel,
  totalCount,
  batch_id,
) => {
  try {
    console.log("processAutomationData", selectedTool, data, selectedModel);
    const token = localStorage.getItem("token");

    if (selectedTool === "Mpn Segregation") {
      return await processAutomationDataWithChunking(
        selectedTool,
        data,
        selectedModel,
        totalCount,
      );
    }
    if (selectedTool === "Enrichment") {
      return await processEnrichmentWithChunking(
        data,
        selectedModel,
        totalCount,
      );
    }
    if (selectedTool === "Duplicate Check") {
      return await processDuplicateCheckWithChunking(
        data,
        selectedModel,
        totalCount,
      );
    }

    const payload = {
      ...data,
      model_name: selectedModel,
      reference_batch_id: data?.data?.[0]?.folderId,
    };
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);

    const response = await axios.post(
      `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/process_records`,
      payload,
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response");

    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) throw new Error(response.data.message);
      return response.data;
    }
  } catch (error) {
    let sendError = error;
    if (sendError.response?.data?.message)
      sendError = new Error(sendError.response.data.message);
    else if (sendError.response?.data?.detail)
      sendError = new Error(sendError.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

const processAutomationDataWithChunking = async (
  selectedTool,
  data,
  selectedModel,
  totalCount,
) => {
  try {
    console.log("Starting chunked processing for", selectedTool);
    const token = localStorage.getItem("token");
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const batchId = data?.data?.[0]?.folderId;

    if (!batchId) throw new Error("Batch ID not found in data");
    console.log(`Processing batch_id: ${batchId}`);

    const totalRecords = totalCount || 0;
    const chunkSize = 200;
    const totalChunks = Math.ceil(totalRecords / chunkSize);

    if (totalChunks === 0) throw new Error("No records found to process");

    let totalProcessedRecords = 0;
    let totalErrors = 0;
    const chunkResults = [];

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const startRecord = chunkIndex * chunkSize;
      const endRecord = Math.min(startRecord + chunkSize, totalRecords);

      try {
        const chunkPayload = {
          data: [
            {
              folderId: batchId,
              originalFile: data.data[0].originalFile,
              fileSize: data.data[0].fileSize,
              fileType: data.data[0].fileType,
              activityType: selectedTool,
              chunkIndex,
              chunkSize,
              startRecord,
              endRecord,
              totalChunks,
              databaseOnly: true,
            },
          ],
          model_name: selectedModel,
        };

        const chunkResponse = await axios.post(
          `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/process_records`,
          chunkPayload,
          {
            headers: { "Content-Type": "application/json", "x-api-key": token },
          },
        );

        if (chunkResponse.status >= 200 && chunkResponse.status < 300) {
          const chunkResult = chunkResponse.data;
          if (window.mpnProgressCallback && chunkResult)
            window.mpnProgressCallback(chunkResult);

          chunkResults.push({
            chunkIndex: chunkIndex + 1,
            recordsProcessed:
              chunkResult.records_processed || endRecord - startRecord,
            status: "completed",
            result: chunkResult,
          });
          totalProcessedRecords +=
            chunkResult.records_processed || endRecord - startRecord;
        } else {
          throw new Error(
            `Chunk ${chunkIndex + 1} failed with status: ${chunkResponse.status}`,
          );
        }
      } catch (chunkError) {
        console.error(
          `Error processing chunk ${chunkIndex + 1}:`,
          chunkError.message,
        );
        totalErrors++;
        chunkResults.push({
          chunkIndex: chunkIndex + 1,
          recordsProcessed: 0,
          status: "failed",
          error: chunkError.message,
        });
        continue;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    const successfulChunks = chunkResults.filter(
      (c) => c.status === "completed",
    ).length;
    const failedChunks = chunkResults.filter(
      (c) => c.status === "failed",
    ).length;

    return {
      status: "completed",
      message: `${selectedTool} chunked processing completed`,
      batch_id: batchId,
      totalRecords,
      totalProcessedRecords,
      chunkSize,
      totalChunks,
      successfulChunks,
      failedChunks,
      totalErrors,
      chunkResults,
      processingMethod: "chunked",
      records_processed: totalProcessedRecords,
      summary: {
        total_processed: totalProcessedRecords,
        total_chunks: totalChunks,
        successful_chunks: successfulChunks,
        failed_chunks: failedChunks,
      },
    };
  } catch (error) {
    console.error("Error in chunked processing:", error.message);
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened during chunked processing. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};
const processEnrichmentWithChunking = async (
  data,
  selectedModel,
  totalCount,
) => {
  const token = localStorage.getItem("token");
  const { apiEndpoint, alias } = getApiConfigForTool("Enrichment");

  const batchId = data?.data?.[0]?.folderId;

  if (!batchId) {
    throw new Error("Batch ID not found");
  }

  const chunkSize = 200;

  let limit = 0;
  let offset = chunkSize;
  let hasMore = true;

  let finalResponse = null;
  while (hasMore) {
    const response = await axios.post(
      `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/process_records`,
      {
        batch_id: batchId,
        limit,
        offset,
        model_name: selectedModel,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": token,
        },
      },
    );

    const chunkResult = response.data;

    console.log("Chunk Result:", chunkResult);

    finalResponse = chunkResult;

    hasMore = chunkResult.has_more;

    if (window.enrichmentProgressCallback) {
      console.log(
        "Updating UI:",
        chunkResult.progress_percentage,
        chunkResult.end_record,
      );

      window.enrichmentProgressCallback(chunkResult);
    }

    if (hasMore) {
      limit = offset;
      offset += chunkSize;
    }
  }
  return {
    status: "completed",
    batch_id: batchId,
    records_processed: finalResponse?.processed_records || totalCount,
    total_records: finalResponse?.total_records || totalCount,
    failed_records: finalResponse?.failed_records || 0,
    message: finalResponse?.message || "Processing completed",
  };
};

/**
 * Duplicate-check processing, adapted from the enrichment chunking pattern.
 *
 * IMPORTANT DIFFERENCE FROM ENRICHMENT:
 * The enrichment backend expects one HTTP call PER chunk (limit/offset),
 * so the frontend loops with axios.post and stops when has_more is false.
 *
 * The duplicate-check backend now processes the ENTIRE batch in a SINGLE
 * call and streams progress back as newline-delimited JSON (ndjson) —
 * one JSON object per line, e.g.:
 *   {"status":"started", "total_records":6402, ...}
 *   {"status":"processing","chunk_index":1,"total_chunks":33,"records_processed":200,...}
 *   {"status":"processing","chunk_index":2,"total_chunks":33,"records_processed":400,...}
 *   ...
 *   {"status":"completed","records_processed":6402,"total_records":6402,...}
 *
 * So instead of a while(hasMore) loop making repeated axios calls, we make
 * ONE fetch call and read the response body as a stream, parsing each
 * line as it arrives and forwarding it to the progress callback.
 *
 * axios doesn't support consuming a streaming response body in the browser
 * the way fetch's ReadableStream does, so this uses fetch directly.
 */

// ─────────────────────────────────────────────────────────────────────────────
// processDuplicateCheckWithChunking — REPLACES the old streaming/ndjson
// version. The backend now returns one plain JSON response per chunk
// (status: "success", has_more, progress_percentage, end_record, ...),
// same shape as processEnrichmentWithChunking above. So this uses the
// same axios while(hasMore) loop pattern instead of fetch + ReadableStream.
// ─────────────────────────────────────────────────────────────────────────────
const processDuplicateCheckWithChunking = async (
  data,
  selectedModel,
  totalCount,
) => {
  const token = localStorage.getItem("token");
  const { apiEndpoint, alias } = getApiConfigForTool("Duplicate Check"); // NOTE: must match the key exactly as defined in toolConfig ("Duplicate Check", with the space)

  const batchId = data?.data?.[0]?.folderId;

  if (!batchId) {
    throw new Error("Batch ID not found");
  }

  const chunkSize = 200;

  let limit = 0;
  let offset = chunkSize;
  let hasMore = true;

  let finalResponse = null;
  while (hasMore) {
    const response = await axios.post(
      `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/process_records`,
      {
        batch_id: batchId,
        limit,
        offset,
        model_name: selectedModel,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": token,
        },
      },
    );

    const chunkResult = response.data;

    console.log("Duplicate Check Chunk Result:", chunkResult);

    finalResponse = chunkResult;

    hasMore = chunkResult.has_more;

    if (window.duplicateCheckProgressCallback) {
      console.log(
        "Updating UI:",
        chunkResult.progress_percentage,
        chunkResult.end_record,
      );

      window.duplicateCheckProgressCallback(chunkResult);
    }

    if (hasMore) {
      limit = offset;
      offset += chunkSize;
    }
  }

  return {
    status: "completed",
    batch_id: batchId,
    records_processed: finalResponse?.end_record || totalCount,
    total_records: finalResponse?.total_records || totalCount,
    failed_records: finalResponse?.failed_records || 0,
    message: finalResponse?.message || "Processing completed",
  };
};
export default processDuplicateCheckWithChunking;


export const checkCountData = async (data, selectedTool, selectedModel) => {
  try {
    const token = localStorage.getItem("token");
    const payload = { ...data, model_name: selectedModel };
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);

    const response = await axios.post(
      `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_check`,
      payload,
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response");
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) throw new Error(response.data.message);
      return response.data;
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

export const downloadProcessedData = async (selectedTool, batchId, model) => {
  try {
    console.log("modelForDownload", model);
    if (!batchId)
      throw new Error("Batch ID is required for downloading processed data.");

    const apiKey = localStorage.getItem("token");
    if (!apiKey)
      throw new Error(
        "API key not found in local storage. Please log in again.",
      );

    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/output_file?model=${encodeURIComponent(model)}&batch_id=${encodeURIComponent(batchId)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", "x-api-key": apiKey },
    });

    if (!response.ok)
      throw new Error(`Server responded with status: ${response.status}`);

    const base64Data = await response.text();
    return { base64Data };
  } catch (error) {
    throw error;
  }
};

export const downloadProcessedDataViaS3 = async (
  selectedTool,
  batchId,
  model,
) => {
  try {
    if (!batchId)
      throw new Error("Batch ID is required for downloading processed data.");

    const apiKey = localStorage.getItem("token");
    if (!apiKey)
      throw new Error(
        "API key not found in local storage. Please log in again.",
      );

    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/output_file_s3?model=${encodeURIComponent(model)}&batch_id=${encodeURIComponent(batchId)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", "x-api-key": apiKey },
    });

    if (!response.ok) {
      let errBody;
      try {
        errBody = await response.json();
      } catch {
        errBody = { detail: await response.text() };
      }
      throw new Error(
        errBody.detail || `Server responded with status: ${response.status}`,
      );
    }

    return response.json(); // { download_url, s3_key, filename, records }
  } catch (error) {
    throw error;
  }
};

export const downloadClassificationOutputFile = async (
  selectedTool,
  batchId,
) => {
  try {
    if (!batchId)
      throw new Error("Batch ID is required for downloading processed data.");

    const apiKey = localStorage.getItem("token");
    if (!apiKey)
      throw new Error(
        "API key not found in local storage. Please log in again.",
      );

    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/output_file_s3?batch_id=${encodeURIComponent(batchId)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", "x-api-key": apiKey },
    });

    if (!response.ok) {
      let errBody;
      try {
        errBody = await response.json();
      } catch {
        errBody = { detail: await response.text() };
      }
      throw new Error(
        errBody.detail || `Server responded with status: ${response.status}`,
      );
    }

    return response.json(); // { status_code, download_url, s3_key, filename, records }
  } catch (error) {
    throw error;
  }
};

export const gethistoryData = async (selectedTool) => {
  try {
    const token = localStorage.getItem("token");
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);

    const response = await axios.get(
      `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/operation_history`,
      {
        params: { tool_name: selectedTool },
        headers: { "Content-Type": "application/json", "x-api-key": token },
      },
    );

    if (response === undefined) throw new Error("No response");
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) throw new Error(response.data.message);
      return response.data;
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

export const getCustomerDropdown = async (selectedTool) => {
  try {
    const token = localStorage.getItem("token");
    const config = getApiConfigForTool(selectedTool);
    const { alias } = config;

    console.log(
      selectedTool,
      `https://devai.conasservices.com/v1/${alias}/${config.apiEndpoint}/dropdown`,
    );

    const response = await axios.get(
      `https://devai.conasservices.com/v1/${alias}/${config.apiEndpoint}/dropdown`,
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response");
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) throw new Error(response.data.message);
      return response.data;
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

export const getDetailData = async (batchId, selectedTool = "Enrichment") => {
  try {
    const token = localStorage.getItem("token");
    const { alias } = getApiConfigForTool(selectedTool);

    const response = await axios.get(
      `https://devai.conasservices.com/v1/${alias}/enrichment/detailhistory?batch_id=${batchId}`,
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response");
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) throw new Error(response.data.message);
      return response.data;
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

export const downloadFile = async (
  status,
  batchId,
  selectedTool = "Enrichment",
) => {
  try {
    const token = localStorage.getItem("token");
    const { alias } = getApiConfigForTool(selectedTool);

    const response = await axios.get(
      `https://devai.conasservices.com/v1/${alias}/enrichment/output_file?status=${status}&batch_id=${batchId}`,
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response === undefined) throw new Error("No response");
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) throw new Error(response.data.message);
      return response.data;
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message)
      sendError = new Error(error.response.data.message);
    else if (error.response?.data?.detail)
      sendError = new Error(error.response.data.detail);
    else
      sendError = new Error(
        "Something unexpected happened. If the issue persists, please contact our support team for assistance.",
      );
    throw sendError;
  }
};

export const updateData = async (data, selectedTool = "Enrichment") => {
  try {
    const token = localStorage.getItem("token");
    const { alias } = getApiConfigForTool(selectedTool);

    const response = await axios.put(
      `https://devai.conasservices.com/v1/${alias}/enrichment/update_records`,
      { ...data },
      { headers: { "Content-Type": "application/json", "x-api-key": token } },
    );

    if (response.status >= 200 && response.status < 300) {
      if (response.data?.status === 204)
        throw new Error(response.data.message || "No data updated.");
      return response.data;
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      "Something unexpected happened. If the issue persists, please contact our support team for assistance.";
    throw new Error(message);
  }
};

export const uploadModelFileToS3 = async (
  file,
  enrichBatchId = null,
  selectedTool,
) => {
  try {
    if (!file) throw new Error("No model file provided");

    const base64Content = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = () => reject("Error reading file");
      reader.readAsDataURL(file);
    });

    const requestBody = {
      base64File: base64Content,
      tool_name: "Data Matching",
      model_name: "Custom Model",
      unique_records: 0,
      total_records: 0,
    };
    if (enrichBatchId) requestBody.custom_batch_id = enrichBatchId;

    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) throw new Error("Authentication token not found");

    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);

    const response = await fetch(
      `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/model_file`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": token },
        body: JSON.stringify(requestBody),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.detail?.message ||
          errorData?.detail ||
          `API request failed with status: ${response.status}`,
      );
    }

    const responseData = await response.json();
    return {
      originalFile: file.name,
      fileSize: file.size,
      fileType: file.type,
      batch_id: responseData.batch_id,
      records_processed: responseData.records_processed,
      model_name: responseData.model_name,
      table_used: responseData.table_used,
      s3Location: null,
      key: null,
      folderId: responseData.batch_id,
    };
  } catch (error) {
    console.error("Error uploading model file via API:", error);
    throw error;
  }
};

export const downloadInputFile = async (selectedTool, batchId) => {
  try {
    if (!batchId)
      throw new Error("Batch ID is required for downloading processed data.");
    const apiKey = localStorage.getItem("token");
    if (!apiKey)
      throw new Error(
        "API key not found in local storage. Please log in again.",
      );
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/input_download?batch_id=${encodeURIComponent(batchId)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", "x-api-key": apiKey },
    });
    if (!response.ok)
      throw new Error(`Server responded with status: ${response.status}`);
    const base64Data = await response.text();
    return { base64Data };
  } catch (error) {
    throw error;
  }
};

export const downloadModelFile = async (selectedTool, batchId) => {
  try {
    if (!batchId)
      throw new Error("Batch ID is required for downloading processed data.");
    const apiKey = localStorage.getItem("token");
    if (!apiKey)
      throw new Error(
        "API key not found in local storage. Please log in again.",
      );
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/model_download?batch_id=${encodeURIComponent(batchId)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", "x-api-key": apiKey },
    });
    if (!response.ok)
      throw new Error(`Server responded with status: ${response.status}`);
    const base64Data = await response.text();
    return { base64Data };
  } catch (error) {
    throw error;
  }
};

export const downloadAllFiles = async (selectedTool, batchId) => {
  try {
    if (!batchId)
      throw new Error("Batch ID is required for downloading processed data.");
    const apiKey = localStorage.getItem("token");
    if (!apiKey)
      throw new Error(
        "API key not found in local storage. Please log in again.",
      );
    const { apiEndpoint, alias } = getApiConfigForTool(selectedTool);
    const url = `https://devai.conasservices.com/v1/${alias}/${apiEndpoint}/allfiles_download?batch_id=${encodeURIComponent(batchId)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", "x-api-key": apiKey },
    });
    if (!response.ok)
      throw new Error(`Server responded with status: ${response.status}`);
    const base64Data = await response.text();
    return { base64Data };
  } catch (error) {
    throw error;
  }
};

const MATCH_BASE_URL =
  "https://devai.conasservices.com/v1/data_match/match_activity";

export const presignInputFile = async (filename, toolName) => {
  const response = await fetch(`${MATCH_BASE_URL}/input_file/presign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key":
        localStorage.getItem("token") || sessionStorage.getItem("token"),
    },
    body: JSON.stringify({ filename, tool_name: toolName }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(
      `Failed to get upload URL: ${response.status} — ${errText}`,
    );
  }

  return response.json(); // { upload_url, s3_key, batch_id }
};

export const uploadFileToS3 = (file, uploadUrl, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);

    const ext = file.name.split(".").pop().toLowerCase();
    const contentTypeMap = {
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      xls: "application/vnd.ms-excel",
      csv: "text/csv",
    };
    xhr.setRequestHeader(
      "Content-Type",
      contentTypeMap[ext] || "application/octet-stream",
    );

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error("Network error during S3 upload"));
    xhr.send(file);
  });
};

export const finalizeInputFileUpload = async ({
  s3Key,
  batchId,
  toolName,
  modelName,
  uniqueRecords,
  totalRecords,
  limit,
  offset,
}) => {
  const response = await fetch(`${MATCH_BASE_URL}/input_file`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key":
        localStorage.getItem("token") || sessionStorage.getItem("token"),
    },
    body: JSON.stringify({
      s3_key: s3Key,
      custom_batch_id: batchId,
      tool_name: toolName,
      model_name: modelName,
      unique_records: uniqueRecords,
      total_records: totalRecords,
      limit,
      offset,
    }),
  });

  if (!response.ok) {
    let errBody;
    try {
      errBody = await response.json();
    } catch {
      errBody = { detail: await response.text() };
    }
    const err = new Error(
      errBody.detail?.message || errBody.detail || "Upload finalize failed",
    );
    err.response = { status: response.status, data: errBody };
    throw err;
  }

  return response.json();
};
